/**
 * Cache lecture API IndexedDB — GO_OFFLINE_FULL W1
 * Complète localStorage (quota ~2,5 Mo) pour listes métier multi-rôles.
 */
const DB_NAME = 'ucp_api_read_cache_v1';
const DB_VERSION = 1;
const STORE = 'entries';
const MAX_ENTRIES = 400;
/** Listes SA lourdes (clients 10k / interventions) — GO_OFFLINE_READ_HEAVY */
const MAX_ENTRY_BYTES = 8 * 1024 * 1024;

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' });
        store.createIndex('by_ts', 'timestamp', { unique: false });
        store.createIndex('by_scope', 'scope', { unique: false });
      }
    };
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('tx aborted'));
  });
}

async function pruneOldest(db, keep = MAX_ENTRIES) {
  const all = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).index('by_ts').getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
  if (all.length <= keep) return;
  const toDelete = all.slice(0, all.length - keep);
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  toDelete.forEach((row) => {
    if (row?.key) store.delete(row.key);
  });
  await txDone(tx);
}

export async function putApiReadCacheEntry({ key, scope, data, timestamp = Date.now() }) {
  if (!key) return false;
  let serialized;
  try {
    serialized = JSON.stringify(data);
  } catch {
    return false;
  }
  if (serialized.length * 2 > MAX_ENTRY_BYTES) return false;

  try {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({
      key,
      scope: scope || 'anonymous',
      data,
      timestamp,
      bytes: serialized.length * 2
    });
    await txDone(tx);
    await pruneOldest(db, MAX_ENTRIES);
    return true;
  } catch (err) {
    console.warn('[UCP] IDB read-cache put failed:', err?.message || err);
    return false;
  }
}

export async function getApiReadCacheEntry(key) {
  if (!key) return null;
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => {
        const row = req.result;
        if (!row || typeof row !== 'object' || !('data' in row)) {
          resolve(null);
          return;
        }
        resolve({ data: row.data, timestamp: row.timestamp || 0 });
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

/**
 * Soft-hit hors ligne : même chemin URL exact (pas de sous-route).
 * Clé = prefix:scope:baseUrl:url:params — évite soft-hit /reports/12/images pour /reports/12.
 */
export async function findApiReadCacheByUrlPath(urlPath, { scope = null } = {}) {
  const needle = String(urlPath || '').trim().replace(/\/+$/, '');
  if (!needle) return null;
  try {
    const db = await openDb();
    const rows = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const store = tx.objectStore(STORE);
      const req = scope
        ? store.index('by_scope').getAll(scope)
        : store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    const marker = `:${needle}:`;
    const subMarker = `:${needle}/`;
    const matches = rows
      .filter((row) => {
        if (!row?.key || row.data == null) return false;
        const key = String(row.key);
        if (key.includes(subMarker)) return false;
        return key.includes(marker) || key.endsWith(`:${needle}`) || key.endsWith(`:${needle}:`);
      })
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    const best = matches[0];
    if (!best) return null;
    return { data: best.data, timestamp: best.timestamp || 0 };
  } catch {
    return null;
  }
}

export async function clearApiReadCache({ scope = null } = {}) {
  try {
    const db = await openDb();
    if (!scope) {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      await txDone(tx);
      return;
    }
    const rows = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).index('by_scope').getAll(scope);
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    rows.forEach((row) => {
      if (row?.key) store.delete(row.key);
    });
    await txDone(tx);
  } catch (err) {
    console.warn('[UCP] IDB read-cache clear failed:', err?.message || err);
  }
}

export const API_READ_CACHE_LIMITS = {
  MAX_ENTRIES,
  MAX_ENTRY_BYTES
};
